//@flow
import React, { useMemo, useRef, useState } from "react";
import { Text, Keyboard, Dimensions, Platform } from "react-native";
import {
  BackButton,
  Box,
  MainButton,
  MainText,
  MainTitle,
  MySafeAreaView,
} from "../../components/common";
import { useSelector } from "react-redux";
import { KeyboardAwareScrollView } from "react-native-keyboard-aware-scroll-view";
import Colors from "@microdoing/shared/constants/Colors";
import i18n from "@microdoing/shared/i18n";
import {
  widthPercentageToDP as wp,
  heightPercentageToDP as hp,
} from "react-native-responsive-screen";
import { getRequesting } from "@microdoing/shared/services/client/selectors";
import { changeSignupInput } from "@microdoing/shared/services/signup/actions";
import Theme from "@microdoing/shared/constants/Theme";
import theme from "app/Theme/Theme";
import { useNavigation } from "@react-navigation/native";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import WebSetupPhoneNumber from "./WebSetupPhoneNumber";
import PhoneInput from "react-native-phone-number-input";
import normalize from "app/utils/normalize";
import { useSafeAreaInsets } from "react-native-safe-area-context";

const { width, height } = Dimensions.get("window");
const aspectRatio = height / width;
const isPad = () => aspectRatio < 1.6;

const WIDTH = wp("85%");

// if change there - dont forget to change the complete signup saga

const SignupPhoneNumber = () => {
  const requesting = useSelector(getRequesting);

  const [error, setError] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [formattedPhoneNumber, setFormattedPhoneNumber] = useState("");

  const insets = useSafeAreaInsets();

  const navigation = useNavigation();
  const phoneInput = useRef(null);

  const numberIsValid = useMemo(() => {
    return phoneInput.current?.isValidNumber(phoneNumber);
  }, [phoneNumber, phoneInput]);

  const goNext = () => {
    if (!numberIsValid) {
      return setError(i18n.t("phoneNumber-invalid"));
    }

    changeSignupInput({
      phoneNumber: formattedPhoneNumber,
    });

    Keyboard.dismiss();
    navigation.navigate("SignupSetupPicture");
  };

  const renderBody = () => {
    return (
      <PhoneInput
        ref={phoneInput}
        placeholder={i18n.t("phoneNumber")}
        defaultCode="FR"
        layout="first"
        onChangeText={text => {
          setPhoneNumber(text);
          setError("");
        }}
        onChangeFormattedText={text => {
          setFormattedPhoneNumber(text);
        }}
        withDarkTheme
        withShadow
        autoFocus
        containerStyle={styles.container}
        textContainerStyle={styles.input}
        codeTextStyle={styles.codeText}
        countryPickerButtonStyle={styles.buttonCountry}
        textInputStyle={{
          borderColor: "transparent",
          outlineWidth: 0,
          color: theme.colors["neutral.800"],
        }}
      />
    );
  };

  if (isWebDesktop) {
    return (
      <WebSetupPhoneNumber
        body={renderBody()}
        isButtonDisable={!numberIsValid}
        goNext={goNext}
      />
    );
  }

  return (
    <MySafeAreaView
      flex={1}
      paddingTop="xxs"
      position="relative"
      alignItems="center"
      justifyContent="center"
      backgroundColor="neutral.white"
    >
      <KeyboardAwareScrollView
        enableOnAndroid
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        contentContainerStyle={styles.scrollView}
      >
        <BackButton
          onPress={() => navigation.goBack()}
          style={styles.backButton}
        />
        <Box
          width={wp("85%")}
          flex={3}
          marginTop={isPad() ? "xs" : "m"}
          justifyContent="flex-start"
          alignItems="flex-start"
          flexDirection="column"
        >
          <Box marginBottom="xm">
            <MainTitle variant="l8-bold" textAlign="left">
              {i18n.t("informations")}
            </MainTitle>
            <MainText
              color="neutral.500"
              variant="l5-medium"
              textAlign="left"
              marginTop="xxs"
            >
              {i18n.t("enter-your-phone-number")}
            </MainText>
          </Box>
          {renderBody()}
          {!!error && <Text style={styles.errorText}>{error}</Text>}
          <Box width="100%" marginTop="sm">
            <MainButton
              isLoading={requesting}
              isDisabled={!numberIsValid}
              text={i18n.t("next")}
              onPress={goNext}
            />
          </Box>
        </Box>
      </KeyboardAwareScrollView>
    </MySafeAreaView>
  );
};

const styles = {
  backButton: {
    alignSelf: "flex-start",
    marginBottom: 10,
    left: Platform.OS === "ios" ? -10 : 0,
    marginTop: 0,
  },
  scrollView: {
    flexGrow: 1,
    backgroundColor: theme.colors["neutral.white"],
    justifyContent: "center",
    alignItems: "center",
    paddingBottom: 25,
  },
  title: {
    marginBottom: 30,
    alignSelf: "flex-start",
    fontSize: Theme.FONT_SIZE_BIG_TITLE,
  },
  text: {
    alignSelf: "flex-start",
    textAlign: "left",
    fontSize: Theme.FONT_SIZE_LARGE,
    marginBottom: 25,
  },
  errorText: {
    fontSize: 14,
    color: "red",
    marginTop: 15,
  },
  roundButton: {
    marginTop: 35,
    backgroundColor: Colors.blue,
    height: 60,
    width: WIDTH,
  },
  roundButtonText: {
    fontSize: wp("4.2%"),
    color: theme.colors["neutral.white"],
  },
  disableButton: {
    borderWidth: 0,
    borderColor: Colors.lightWhite,
    backgroundColor: theme.colors["neutral.500"],
  },
  disableButtonText: {
    fontSize: wp("4.2%"),
    color: theme.colors["neutral.white"],
  },
  container: {
    shadowOpacity: 0,
    height: normalize(60),
    width: "100%",
  },
  input: {
    backgroundColor: theme.colors["neutral.white"],
    borderColor: theme.colors["neutral.100"],
    borderWidth: 2,
    borderLeftWidth: 0,
    color: theme.colors["neutral.900"],
    borderBottomRightRadius: normalize(8),
    borderTopRightRadius: normalize(8),
    padding: 0,
  },
  buttonCountry: {
    backgroundColor: theme.colors["neutral.white"],
    borderColor: theme.colors["neutral.100"],
    borderWidth: 2,
    borderBottomLeftRadius: normalize(8),
    borderTopLeftRadius: normalize(8),
    maxWidth: normalize(80),
  },
  codeText: {
    color: theme.colors["neutral.900"],
  },
};

export default SignupPhoneNumber;
